'use client';

import { type CSSProperties, type FC, type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { ENTERPRISE_WATERMARK_URL, isEnterpriseDistribution, SITE } from '@/app/constants';
import { cn } from '@/app/utils';

/**
 * 媒体水印容器组件 Props
 */
export interface MediaWithWatermarkProps {
  /** 子元素内容（可以是图片、视频或任何媒体元素） */
  children?: ReactNode;
  /** 媒体内容宽度（用于计算水印位置与 contain 适配） */
  width: number;
  /** 媒体内容高度（用于计算水印位置与 contain 适配） */
  height: number;
  /** 作用于内层媒体盒的 className */
  className?: string;
  /** 内层媒体盒额外的样式 */
  style?: CSSProperties;
  /** 是否展示水印 */
  showWatermark: boolean;
  /**
   * 仅叠加水印（absolute inset-0），不参与 contain 尺寸计算。
   * 适用于 grid 卡片、aspect-ratio 容器、object-cover 等外层已定型场景。
   */
  overlay?: boolean;
}

const CN_WATERMARK_URL = 'https://image01-aliyun.vidu.zone/vidu/media-asset/watermark-cn-9cfdadb8.webp';
const GLOBAL_WATERMARK_URL = 'https://image01.cf.vidu.studio/vidu/media-asset/watermark-645d2032.webp';

/**
 * 按媒体与容器宽高比选择 contain 约束边。
 * 容器高度未知时（grid 首帧、父级 height:auto）退化为宽度撑满 + aspect-ratio 自算高度。
 */
export function getMediaContainFitClassName(
  mediaWidth: number,
  mediaHeight: number,
  containerWidth: number,
  containerHeight: number,
): string {
  if (mediaWidth <= 0 || mediaHeight <= 0) {
    return 'max-h-full max-w-full';
  }

  const mediaAspect = mediaWidth / mediaHeight;

  if (containerWidth > 0 && containerHeight <= 0) {
    return 'w-full max-h-full';
  }

  if (containerHeight > 0 && containerWidth <= 0) {
    return 'h-full w-auto max-w-full';
  }

  if (containerWidth <= 0 && containerHeight <= 0) {
    return 'w-full max-h-full';
  }

  const containerAspect = containerWidth / containerHeight;
  return mediaAspect > containerAspect ? 'w-full max-h-full' : 'h-full w-auto max-w-full';
}

/** 观测父级分配区域；无父级时退回观测自身。grid 下通常能拿到列宽，高度可能为 0。 */
function useContainerSize(elementRef: RefObject<HTMLDivElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });

    const parent = element.parentElement;
    observer.observe(parent instanceof HTMLElement ? parent : element);

    return () => observer.disconnect();
  }, []);

  return size;
}

/**
 * 带水印的媒体内容容器组件
 *
 * 默认模式：外层观测父级宽高，内层按 contain 规则居中适配。
 * overlay 模式：仅 absolute 叠加水印。
 */
export const MediaWithWatermark: FC<MediaWithWatermarkProps> = ({
  children,
  width,
  height,
  className,
  style,
  showWatermark,
  overlay = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useContainerSize(containerRef);

  const watermarkSrc = showWatermark
    ? isEnterpriseDistribution
      ? ENTERPRISE_WATERMARK_URL
      : SITE === 'china'
        ? CN_WATERMARK_URL
        : GLOBAL_WATERMARK_URL
    : null;
  const aspectRatio = width / height;
  const isLandscape = width > height;

  const watermarkStyle: CSSProperties = isLandscape
    ? {
        height: '7%',
        bottom: '2%',
        right: `${2 / aspectRatio}%`,
        width: 'auto',
      }
    : {
        height: `${7 * aspectRatio}%`,
        bottom: `${2 * aspectRatio}%`,
        right: '2%',
        width: 'auto',
      };

  const watermark = watermarkSrc ? (
    <img
      src={watermarkSrc}
      alt="watermark"
      className="pointer-events-none absolute select-none"
      style={watermarkStyle}
    />
  ) : null;

  if (overlay) {
    return (
      <div className={cn('pointer-events-none absolute inset-0', className)} style={style}>
        {children}
        {watermark}
      </div>
    );
  }

  const fitClassName = getMediaContainFitClassName(width, height, containerSize.width, containerSize.height);
  const hasBoundedHeight = containerSize.height > 0;

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex w-full min-w-0 items-center justify-center self-stretch',
        hasBoundedHeight ? 'h-full min-h-0' : 'h-auto',
      )}
    >
      <div
        className={cn('relative', fitClassName, className)}
        style={{
          ...style,
          aspectRatio: `${width} / ${height}`,
        }}
      >
        {children}
        {watermark}
      </div>
    </div>
  );
};
